import React, { useEffect, useState } from 'react';
import {
  Building2, Users, Link2, Plus, Trash2, AlertCircle, CheckCircle2, Search
} from 'lucide-react';
import api from '../../services/api';

interface Client {
  id: number;
  company_name: string;
  email: string;
  client_type: 'independent' | 'group' | 'central';
}

interface CentralMembership {
  id: number;
  central_buyer_id: number;
  member_buyer_id: number;
  created_at: string;
}

const CentralManagementTailwind: React.FC = () => {
  const [buyers, setBuyers] = useState<Client[]>([]);
  const [memberships, setMemberships] = useState<CentralMembership[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Modal d'ajout d'affiliation
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedCentral, setSelectedCentral] = useState<number | null>(null);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    setBusy(true);
    try {
      const [resBuyers, resMemberships] = await Promise.all([
        api.get('/clients'),
        api.get('/central-memberships'),
      ]);
      
      setBuyers(Array.isArray(resBuyers.data) ? resBuyers.data : []);
      setMemberships(Array.isArray(resMemberships.data) ? resMemberships.data : []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setBusy(false);
    }
  };

  const updateBuyerType = async (clientId: number, newType: 'independent' | 'group' | 'central'): Promise<void> => {
    try {
      await api.patch(`/clients/${clientId}`, { client_type: newType });
      setSuccess(`Type d'client mis à jour avec succès`);
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (e: any) {
      console.error('Update error:', e);
      setError(e.response?.data?.detail || 'Erreur lors de la mise à jour');
      setTimeout(() => setError(''), 5000);
    }
  };

  const createMembership = async (): Promise<void> => {
    if (!selectedCentral || !selectedMember) {
      setError('Veuillez sélectionner une centrale et un contact');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/central-memberships', {
        central_buyer_id: selectedCentral,
        member_buyer_id: selectedMember,
      });
      
      setSuccess('Affiliation créée avec succès');
      setTimeout(() => setSuccess(''), 3000);
      setShowModal(false);
      setSelectedCentral(null);
      setSelectedMember(null);
      await loadData();
    } catch (e: any) {
      console.error('Create membership error:', e);
      setError(e.response?.data?.detail || 'Erreur lors de la création');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMembership = async (id: number): Promise<void> => {
    if (!confirm('Supprimer cette affiliation ?')) return;
    
    try {
      await api.delete(`/central-memberships/${id}`);
      setSuccess('Affiliation supprimée');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (e: any) {
      console.error('Delete error:', e);
      setError(e.response?.data?.detail || 'Erreur lors de la suppression');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getBuyerName = (id: number): string => {
    const Client = buyers.find(b => b.id === id);
    return Client ? Client.company_name : `Client #${id}`;
  };

  const centrals = buyers.filter(b => b.client_type === 'central');
  const nonCentrals = buyers.filter(b => b.client_type !== 'central');

  const filteredBuyers = buyers.filter(b => 
    !search || b.company_name.toLowerCase().includes(search.toLowerCase()) || b.email.toLowerCase().includes(search.toLowerCase())
  );

  const typeLabels = {
    independent: { label: 'Indépendant', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300' },
    group: { label: 'Groupe', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300' },
    central: { label: 'Centrale', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' },
  };

  if (busy) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-indigo-50/20 to-slate-50 dark:from-slate-950 dark:via-indigo-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <Link2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Gestion des Centrales</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {centrals.length} centrale{centrals.length > 1 ? 's' : ''} • {memberships.length} affiliation{memberships.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 font-medium">
          <Plus className="h-4 w-4" />
          Nouvelle Affiliation
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Messages */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input 
              type="text" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder="Rechercher un client..." 
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" 
            />
          </div>

          {/* Liste des clients avec type */}
          <div className="p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              Types d'clients ({filteredBuyers.length})
            </h3>
            <div className="space-y-3">
              {filteredBuyers.map(Client => (
                <div key={Client.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                  <div className="flex-1">
                    <h4 className="font-medium">{Client.company_name}</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{Client.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeLabels[Client.client_type].color}`}>
                      {typeLabels[Client.client_type].label}
                    </span>
                    <select 
                      className="px-3 py-1 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
                      value={Client.client_type}
                      onChange={(e) => updateBuyerType(Client.id, e.target.value as any)}
                    >
                      <option value="independent">Indépendant</option>
                      <option value="group">Groupe</option>
                      <option value="central">Centrale</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Affiliations existantes */}
          <div className="p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Affiliations ({memberships.length})
            </h3>
            {memberships.length === 0 ? (
              <p className="text-center py-8 text-slate-500 dark:text-slate-400">
                Aucune affiliation. Créez-en une pour permettre l'héritage des BPUs.
              </p>
            ) : (
              <div className="space-y-3">
                {memberships.map(membership => (
                  <div key={membership.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">Centrale</div>
                        <div className="font-medium text-green-700 dark:text-green-300">
                          {getBuyerName(membership.central_buyer_id)}
                        </div>
                      </div>
                      <div className="text-2xl text-slate-400">→</div>
                      <div className="text-center">
                        <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">contact</div>
                        <div className="font-medium text-blue-700 dark:text-blue-300">
                          {getBuyerName(membership.member_buyer_id)}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => deleteMembership(membership.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'ajout d'affiliation */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Link2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Nouvelle Affiliation</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Lier un client à une centrale</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Centrale d'achat *</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                  value={selectedCentral || ''}
                  onChange={(e) => setSelectedCentral(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">Sélectionner une centrale</option>
                  {centrals.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
                {centrals.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Aucune centrale disponible. Créez d'abord un client de type "Centrale".
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Client membre *</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                  value={selectedMember || ''}
                  onChange={(e) => setSelectedMember(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">Sélectionner un membre</option>
                  {nonCentrals.map(b => (
                    <option key={b.id} value={b.id}>{b.company_name}</option>
                  ))}
                </select>
              </div>

              <div className="p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <p className="text-sm text-indigo-800 dark:text-indigo-200">
                  <strong>Note:</strong> Une fois affilié, le membre héritera automatiquement des bpus de la centrale. 
                  Il pourra toujours définir ses propres bpus qui auront la priorité.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  Annuler
                </button>
                <button 
                  onClick={createMembership}
                  disabled={submitting || !selectedCentral || !selectedMember}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-400 transition-colors font-medium disabled:cursor-not-allowed"
                >
                  {submitting ? 'Création...' : 'Créer l\'affiliation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentralManagementTailwind;
